import {
	AbilityBuilder,
	createMongoAbility,
	type MongoAbility,
} from '@casl/ability'
import { AbilityProvider as CaslAbilityProvider } from '@casl/react'
import {
	type ComponentType,
	createContext,
	createElement,
	type ReactNode,
	useContext,
} from 'react'
import type { AuthContextProps } from 'react-oidc-context'
import type { UserPermissionResponse } from '@/generated/api/backstage.schemas'
import {
	getPermissionSubjectForEntity,
	getPermissionSubjectForEntityTab,
	getPermissionSubjectForToken,
} from '@/generated/entity-config'

export type AppAction = 'read' | 'write' | 'modify'
export type AppAbility = MongoAbility<[AppAction, string]>

export interface PermissionLike {
	name?: unknown
	applicationScope?: unknown
}

const permissionNamePattern =
	/^(?<subject>[A-Za-z0-9]+)_(?<action>READ|WRITE|MODIFY|Read|Write|Modify|read|write|modify)$/

const actionByToken: Record<string, AppAction> = {
	READ: 'read',
	Read: 'read',
	read: 'read',
	WRITE: 'write',
	Write: 'write',
	write: 'write',
	MODIFY: 'modify',
	Modify: 'modify',
	modify: 'modify',
}

function parsePermissionName(permissionName: unknown): {
	action: AppAction
	subjectToken: string
} | null {
	if (typeof permissionName !== 'string') {
		return null
	}

	const match = permissionNamePattern.exec(permissionName)
	if (!match?.groups) {
		return null
	}

	const action = actionByToken[match.groups.action]
	if (!action) {
		return null
	}

	return {
		action,
		subjectToken: match.groups.subject,
	}
}

function normalizePermissionClaimItem(item: unknown): PermissionLike | null {
	if (typeof item === 'string') {
		return { name: item }
	}

	if (!item || typeof item !== 'object') {
		return null
	}

	const record = item as Record<string, unknown>
	return {
		name: record.name,
		applicationScope: record.applicationScope,
	}
}

function normalizePermissionClaimValue(value: unknown): PermissionLike[] {
	if (Array.isArray(value)) {
		return value
			.map(normalizePermissionClaimItem)
			.filter((entry): entry is PermissionLike => entry !== null)
	}

	if (typeof value === 'string') {
		try {
			const parsed = JSON.parse(value)
			return normalizePermissionClaimValue(parsed)
		} catch {
			return [{ name: value }]
		}
	}

	return []
}

function serializePermissionKeyPart(value: unknown): string {
	if (value === null || value === undefined) {
		return ''
	}

	if (
		typeof value === 'string' ||
		typeof value === 'number' ||
		typeof value === 'boolean' ||
		typeof value === 'bigint'
	) {
		return String(value)
	}

	try {
		const serialized = JSON.stringify(value)
		return serialized ?? ''
	} catch {
		return ''
	}
}

export function extractPermissionsFromAuth(
	auth?: Pick<AuthContextProps, 'user'> | null,
): PermissionLike[] {
	const profile = auth?.user?.profile as Record<string, unknown> | undefined
	if (!profile) {
		return []
	}

	const authorization =
		profile.authorization && typeof profile.authorization === 'object'
			? (profile.authorization as Record<string, unknown>)
			: undefined

	const claimCandidates: unknown[] = [
		profile.permissions,
		profile.permission,
		profile.app_permissions,
		profile.appPermissions,
		authorization?.permissions,
	]

	const permissions = claimCandidates.flatMap(normalizePermissionClaimValue)
	const deduped = new Map<string, PermissionLike>()

	for (const permission of permissions) {
		const key = `${serializePermissionKeyPart(permission.name)}|${serializePermissionKeyPart(permission.applicationScope)}`
		if (!deduped.has(key)) {
			deduped.set(key, permission)
		}
	}

	return [...deduped.values()]
}

export function createAppAbility(
	permissions: UserPermissionResponse[],
): AppAbility {
	const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility)

	const grantPermission = (action: AppAction, subject: string) => {
		can(action, subject)

		// Higher privileges imply read access for the same subject.
		if (action === 'modify') {
			can('write', subject)
			can('read', subject)
		}

		if (action === 'write') {
			can('read', subject)
		}
	}

	for (const permission of permissions) {
		const parsed = parsePermissionName(permission.name)
		if (!parsed) {
			continue
		}

		const subject = getPermissionSubjectForToken(parsed.subjectToken)
		if (!subject) {
			continue
		}

		const resolvedSubject = String(subject)
		grantPermission(parsed.action, resolvedSubject)

		const entitySubject = getPermissionSubjectForEntity(resolvedSubject)
		const detailSubject = entitySubject
			? getPermissionSubjectForEntityTab(String(entitySubject), 'detail')
			: undefined
		if (detailSubject) {
			grantPermission(parsed.action, String(detailSubject))
		}
	}

	return build()
}

function createDenyAllAbility(): AppAbility {
	return createMongoAbility<AppAbility>([])
}

export const AppAbilityContext = createContext<AppAbility>(
	createDenyAllAbility(),
)

const TypedCaslAbilityProvider =
	CaslAbilityProvider as unknown as ComponentType<{
		value: AppAbility
		children?: ReactNode
	}>

export function AppAbilityProvider({
	value,
	children,
}: {
	value: AppAbility
	children: ReactNode
}) {
	return createElement(
		AppAbilityContext.Provider,
		{ value },
		createElement(TypedCaslAbilityProvider, { value }, children),
	)
}

export function useAppAbility(): AppAbility {
	return useContext(AppAbilityContext)
}
